/**
 * Cache de respostas da API com stale-while-revalidate.
 *
 * Vive apenas em memória, de propósito. As respostas do console carregam
 * dados pessoais (e-mails, licenças, pagamentos); gravá-las em
 * sessionStorage/localStorage as deixaria legíveis para qualquer script da
 * origem e sobreviventes ao fim da sessão. O cache morre junto com a aba, e a
 * navegação dentro da sessão continua instantânea.
 */

type Entry = { data: unknown; ts: number };

const mem = new Map<string, Entry>();
const inflight = new Map<string, Promise<unknown>>();
const listeners = new Map<string, Set<() => void>>();

/** Além disso o dado é considerado velho e revalidado. Ainda assim é exibido. */
export const DEFAULT_TTL = 60_000;

export function cacheGet<T>(key: string): { data: T; ts: number } | null {
  const entry = mem.get(key);
  return entry ? { data: entry.data as T, ts: entry.ts } : null;
}

export function cacheSet(key: string, data: unknown) {
  mem.set(key, { data, ts: Date.now() });
  listeners.get(key)?.forEach((fn) => fn());
}

/** Invalida chaves cujo caminho começa com o prefixo (ex.: depois de um POST). */
export function cacheInvalidate(prefix: string) {
  for (const key of [...mem.keys()]) {
    if (key.startsWith(prefix)) {
      mem.delete(key);
      listeners.get(key)?.forEach((fn) => fn());
    }
  }
}

/**
 * Apaga todo o estado em memória. Chamado ao sair e sempre que a sessão cai,
 * para que nada do operador anterior sobreviva à troca de conta.
 */
export function cacheClear() {
  mem.clear();
  inflight.clear();
  listeners.forEach((set) => set.forEach((fn) => fn()));
}

export function cacheSubscribe(key: string, fn: () => void) {
  let set = listeners.get(key);
  if (!set) {
    set = new Set();
    listeners.set(key, set);
  }
  set.add(fn);
  return () => {
    set!.delete(fn);
    if (set!.size === 0) listeners.delete(key);
  };
}

/**
 * Deduplica requisições concorrentes: se a mesma chave já estiver voando,
 * devolve a promise existente em vez de abrir uma segunda conexão.
 */
export function dedupe<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
  const existing = inflight.get(key);
  if (existing) return existing as Promise<T>;

  const promise = fetcher()
    .then((data) => {
      cacheSet(key, data);
      return data;
    })
    .finally(() => {
      inflight.delete(key);
    });

  inflight.set(key, promise);
  return promise;
}

export function isStale(ts: number, ttl = DEFAULT_TTL) {
  return Date.now() - ts > ttl;
}
